import { Link, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { ChevronsDown, ChevronsRight, RotateCcw } from "lucide-react";
import {
  ProjectFilter,
  RoleFilter,
  ScopeStatusFilter,
  TeamsFilter,
} from "@/app/components/SidebarFilters";
import { SystemStatus } from "@/app/components/SystemStatus";
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/app/components/ui/sidebar";
import { useDagStore } from "@/app/hooks/useDagStore";
import { useFlowStore } from "@/app/hooks/useFlowStore";
import { omitDefaultSearchParams, SEARCH_DEFAULTS } from "@/app/routes/index.route";
import { SlackNewsFilters } from "./SlackNewsFilters";

export function AppSidebar() {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const isDashboard = currentPath === "/";

  // Always call hooks unconditionally
  const search = useSearch({ strict: false });
  const slackFilters = useDagStore((state) => state.slackFilters);
  const setSlackFilters = useDagStore((state) => state.setSlackFilters);
  const storeCardsCollapsed = useDagStore((state) => state.cardsCollapsed);
  const storeToggleCardsCollapsed = useDagStore((state) => state.toggleCardsCollapsed);
  const { resetFlowStore } = useFlowStore();
  const navigate = useNavigate({ from: "/" });

  const cardsCollapsed = isDashboard
    ? (search?.cardsCollapsed ?? SEARCH_DEFAULTS.cardsCollapsed)
    : storeCardsCollapsed;

  const toggleCardsCollapsed = () => {
    if (isDashboard && search) {
      void navigate({
        search: omitDefaultSearchParams({ ...search, cardsCollapsed: !cardsCollapsed }),
      });
    } else {
      storeToggleCardsCollapsed();
    }
  };

  const navigationItems = [
    { title: "Dashboard", url: "/", icon: "📊" },
    { title: "Workstreams", url: "/workstreams", icon: "📅" },
    { title: "Slack News", url: "/slack-news", icon: "💬" },
    { title: "GitHub News", url: "/github-news", icon: "🚀" },
    { title: "Flow View", url: "/flow", icon: "🔗" },
    { title: "Org Chart", url: "/orgchart", icon: "🎯" },
    { title: "Draw", url: "/draw", icon: "✏️" },
    // { title: "Timeline", url: "/timeline", icon: "📅" }, // --- Disabled: needs ETL pipeline implementation
    // { title: "Workload", url: "/workload", icon: "⚡" }, // --- Use React flow next time for graph
  ];

  return (
    <>
      <SidebarHeader>
        <div className="flex flex-col gap-2 px-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Team Dash</h2>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Navigation Menu */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={currentPath === item.url}>
                    <Link to={item.url}>
                      <span className="mr-2">{item.icon}</span>
                      {item.title}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        {/* Global Filters - Show on dashboard and workload routes */}
        {(currentPath === "/" || currentPath === "/workload") && (
          <SidebarGroup>
            <SidebarGroupLabel>Filters</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={toggleCardsCollapsed}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-stone-700 hover:text-stone-900 hover:bg-stone-100 rounded-md transition-colors w-full"
                >
                  {cardsCollapsed ? (
                    <ChevronsRight className="w-4 h-4" />
                  ) : (
                    <ChevronsDown className="w-4 h-4" />
                  )}
                  {cardsCollapsed ? "Expand All Cards" : "Collapse All Cards"}
                </button>
                <ScopeStatusFilter className="pl-2" />
                <RoleFilter className="pl-2" />
                <TeamsFilter className="pl-2" />
                <ProjectFilter className="pl-2" />
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {currentPath === "/slack-news" && (
          <SidebarGroup>
            <SidebarGroupLabel>Filters</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="space-y-6">
                <SlackNewsFilters filters={slackFilters} onFiltersChange={setSlackFilters} />
                <TeamsFilter className="pl-2" />
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Flow View Filters */}
        {currentPath === "/flow" && (
          <SidebarGroup>
            <SidebarGroupLabel>Filters</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="space-y-6">
                <TeamsFilter className="pl-2" />
                <button
                  onClick={() => resetFlowStore()}
                  className="ml-2 w-[calc(100%-0.5rem)] bg-stone-100 hover:bg-stone-200 rounded-lg px-3 py-2 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  title="Reset graph and reload data"
                >
                  <RotateCcw className="w-4 h-4 text-stone-600" />
                  <span className="text-sm font-medium text-stone-700">Reset</span>
                </button>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Org Chart Filters */}
        {currentPath === "/orgchart" && (
          <SidebarGroup>
            <SidebarGroupLabel>Filters</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="space-y-6">
                <TeamsFilter className="pl-2" />
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SystemStatus />
      </SidebarFooter>
    </>
  );
}
